import React, { useState } from 'react';
import { invoiceService } from '../services/invoiceService';
import { Language, PDF_LANGUAGE_OPTIONS } from '../types/language';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../hooks/useTranslation';

interface PdfViewerProps {
  companyId: string;
  invoiceId: string;
  invoiceNumber: string;
  clientName: string;
  pdfLanguage?: string; // Сохраненный язык фактуры
  isOpen: boolean;
  onClose: () => void;
}

const PdfViewer: React.FC<PdfViewerProps> = ({ 
  companyId, 
  invoiceId, 
  invoiceNumber, 
  clientName,
  pdfLanguage,
  isOpen, 
  onClose 
}) => {
  const { language: systemLanguage } = useLanguage();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  // Используем сохраненный язык из фактуры, если он есть, иначе системный язык
  const initialLanguage = (pdfLanguage as Language) || systemLanguage || Language.PL_ONLY;
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(initialLanguage);

  // Обновляем выбранный язык при открытии модалки, если есть сохраненный язык
  React.useEffect(() => {
    if (isOpen) {
      if (pdfLanguage) {
        // Используем сохраненный язык из фактуры
        setSelectedLanguage(pdfLanguage as Language);
      } else {
        // Используем системный язык или дефолтный
        setSelectedLanguage(systemLanguage || Language.PL_ONLY);
      }
    }
  }, [isOpen, pdfLanguage, systemLanguage]);

  const loadPdf = React.useCallback(async (language: Language = selectedLanguage) => {
    setIsLoading(true);
    try {
      // Очищаем предыдущий URL
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }
      
      // Всегда передаем выбранный язык в URL, чтобы бэкенд использовал его
      // Бэкенд приоритизирует сохраненный язык, но если мы передаем параметр, он использует его
      const url = `/api/companies/${companyId}/invoices/${invoiceId}/pdf?language=${language}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
      }
    } catch (error) {
      console.error('Error loading PDF:', error);
    } finally {
      setIsLoading(false);
    }
  }, [companyId, invoiceId, pdfUrl, selectedLanguage, pdfLanguage]);

  const downloadPdf = async () => {
    setIsLoading(true);
    try {
      await invoiceService.downloadInvoicePdf(companyId, invoiceId, invoiceNumber, clientName, selectedLanguage);
    } catch (error) {
      console.error('Error downloading PDF:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLanguageChange = (language: Language) => {
    setSelectedLanguage(language);
    loadPdf(language);
  };

  React.useEffect(() => {
    if (isOpen && !pdfUrl) {
      // При открытии модалки загружаем PDF с текущим выбранным языком
      // selectedLanguage уже установлен из pdfLanguage или systemLanguage
      loadPdf(selectedLanguage);
    }
  }, [isOpen, pdfUrl, selectedLanguage, loadPdf]);

  React.useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      {/* Модалка с пропорциями A4 и 80% высоты окна */}
      <div 
        className="bg-white rounded-lg shadow-xl flex flex-col"
        style={{
          width: 'min(90vw, 80vh * 0.707)', // A4 пропорции: ширина = высота * 0.707
          height: '80vh',
          maxWidth: '90vw',
          maxHeight: '80vh'
        }}
      >
        {/* Заголовок */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('invoices.pdfViewer.title')} {invoiceNumber}
          </h2>
          <div className="flex items-center space-x-3">
            {/* Селектор языка */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">{t('invoices.pdfViewer.language')}:</label>
              <select
                value={selectedLanguage}
                onChange={(e) => handleLanguageChange(e.target.value as Language)}
                className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {PDF_LANGUAGE_OPTIONS.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.nativeName}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={downloadPdf}
                disabled={isLoading}
                className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {isLoading ? t('invoices.pdfViewer.downloading') : t('invoices.pdfViewer.download')}
              </button>
              <button
                onClick={onClose}
                className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600 transition-colors"
              >
                {t('invoices.pdfViewer.close')}
              </button>
            </div>
          </div>
        </div>

        {/* Контент PDF */}
        <div className="flex-1 overflow-hidden">
          {isLoading && !pdfUrl ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">{t('invoices.pdfViewer.loading')}</p>
              </div>
            </div>
          ) : pdfUrl ? (
            <iframe
              src={pdfUrl}
              className="w-full h-full border-0"
              title={`PDF ${t('invoices.pdfViewer.title').toLowerCase()} ${invoiceNumber}`}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <p className="text-gray-600 mb-4">{t('invoices.pdfViewer.notLoaded')}</p>
                <button
                  onClick={() => loadPdf()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('invoices.pdfViewer.loadPdf')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PdfViewer;
